import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router';
import { LogOut, MessageSquare } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * App chrome for the redesign — topbar + app nav (blueprint §4: "standard
 * nav labels with poetic accents only in headings"). The rail is grouped
 * into the daily loop (bd Vidi-w9i): LEARN (Home / Create / Review /
 * Library), PROGRESS (Stats, Achievements), and EXPLORE (Guides, Word
 * Packs). Changelog stays out of the rail (footer / palette). Hick's F3:
 * those four routes used to live only behind Cmd+K.
 *
 * Born as Home's route-local HomeChrome under the 2026-07-30 parallel-run
 * freeze; CONSOLIDATED here by the Library full-bleed port (Vidi-q1p), the
 * second consumer, now that the freeze's ports have landed. Every full-bleed
 * redesign route renders this chrome; Vidi-tui's flip makes it the shell.
 * Styles live in src/redesign/system/chrome.css (`.rdh-*` prefix kept from
 * the Home original — the home e2e spec asserts on those classes).
 *
 * Labels arrive as a `t` PROP, never via useTranslation here: this file sits
 * in src/redesign/components/, so vite's manualChunks law packs it (and any
 * node_modules it imports) into Design-shared — react-i18next landing there
 * made every production chunk import Design-shared and leaked redesign
 * assets to flag-off users (caught by the home flag-isolation e2e).
 *
 * Links go to real production routes: until those routes are ported the
 * learner lands on the old design there — expected mid-migration (§6: a
 * route is old or new, never half).
 */
type ChromeT = (key: string, options?: Record<string, unknown>) => string;

export function AppTopbar({
  t,
  displayName,
  targetLanguage,
  dateLine,
  showCreateCta = true,
}: {
  t: ChromeT;
  displayName: string;
  targetLanguage: string | undefined;
  dateLine: string;
  /** Hide on /create so lime is only the current step, not a second Create pill. */
  showCreateCta?: boolean;
}) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [signOutPending, setSignOutPending] = useState(false);
  const [signOutError, setSignOutError] = useState('');

  const handleSignOut = async () => {
    if (signOutPending) return;
    setSignOutPending(true);
    setSignOutError('');
    try {
      await signOut();
      void navigate('/login');
    } catch {
      setSignOutError(t('common:errors.signOutFailedGeneric'));
    } finally {
      setSignOutPending(false);
    }
  };

  return (
    <header className="rdh-topbar">
      <a href="#main-content" className="rdh-skip">
        {t('redesign.topbar.skipToContent')}
      </a>
      <Link to="/home" className="rdh-brand" aria-label={t('redesign.topbar.brandAria')}>
        vid<span>i</span>
      </Link>
      <p className="rdh-date">
        {dateLine}
        {targetLanguage ? ` · ${targetLanguage}` : ''}
      </p>
      <div className="rdh-actions">
        {showCreateCta ? (
          <Link to="/create" className="rdh-create" aria-label={t('redesign.topbar.createMemory')}>
            <span className="rdh-create-full">{t('redesign.topbar.createMemory')}</span>
            <span className="rdh-create-short">{t('common:nav.create')}</span>
          </Link>
        ) : null}
        <Link
          to="/feedback"
          className="rdh-feedback"
          aria-label={t('common:nav.feedback')}
          title={t('common:nav.feedback')}
        >
          <MessageSquare size={20} aria-hidden="true" />
        </Link>
        <Link to="/settings" className="rdh-avatar" aria-label={t('redesign.topbar.profileAria')}>
          {(displayName || 'L').slice(0, 1).toUpperCase()}
        </Link>
        <button
          type="button"
          className="rdh-signout"
          aria-label={t('common:actions.signOut')}
          title={t('common:actions.signOut')}
          aria-busy={signOutPending}
          disabled={signOutPending}
          onClick={handleSignOut}
        >
          <LogOut size={20} aria-hidden="true" />
          <span className="rdh-signout-label">{t('common:actions.signOut')}</span>
        </button>
        <span className="rdh-signout-error" role="alert" aria-live="assertive">
          {signOutError}
        </span>
      </div>
    </header>
  );
}

export function AppNav({ t, dueCount }: { t: ChromeT; dueCount: number }) {
  return (
    <nav className="rdh-nav" aria-label={t('home:redesign.nav.aria')}>
      {/* LEARN — the daily loop, in loop order (bd Vidi-w9i). The group
          wrappers are display:contents (chrome.css) so links stay direct
          children of the nav's block/flex layout in every breakpoint. */}
      <div className="rdh-nav-group">
        <p className="rdh-nav-kicker rd-tiny-label">{t('common:nav.groupLearn')}</p>
        <NavLink to="/home" end className={({ isActive }) => (isActive ? 'is-active' : '')}>
          {t('common:nav.home')}
        </NavLink>
        <NavLink to="/create" className={({ isActive }) => (isActive ? 'is-active' : '')}>
          {t('common:nav.create')}
        </NavLink>
        <NavLink to="/review" className={({ isActive }) => (isActive ? 'is-active' : '')}>
          {t('common:nav.review')}
          {dueCount > 0 && (
            <span
              className="rdh-nav-count"
              aria-label={t('home:redesign.nav.dueCount', { count: dueCount })}
            >
              {dueCount}
            </span>
          )}
        </NavLink>
        <NavLink to="/library" className={({ isActive }) => (isActive ? 'is-active' : '')}>
          {t('common:nav.library')}
        </NavLink>
      </div>
      <div className="rdh-nav-group">
        <p className="rdh-nav-kicker rd-tiny-label">{t('common:nav.groupProgress')}</p>
        <NavLink to="/stats" className={({ isActive }) => (isActive ? 'is-active' : '')}>
          {t('common:nav.stats')}
        </NavLink>
        <NavLink to="/achievements" className={({ isActive }) => (isActive ? 'is-active' : '')}>
          {t('common:nav.achievements')}
        </NavLink>
      </div>
      <div className="rdh-nav-group">
        <p className="rdh-nav-kicker rd-tiny-label">{t('common:nav.groupExplore')}</p>
        <NavLink to="/guides" className={({ isActive }) => (isActive ? 'is-active' : '')}>
          {t('common:nav.guides')}
        </NavLink>
        <NavLink to="/feedback" className={({ isActive }) => (isActive ? 'is-active' : '')}>
          {t('common:nav.feedback')}
        </NavLink>
        <NavLink to="/word-pack" className={({ isActive }) => (isActive ? 'is-active' : '')}>
          {t('common:nav.wordPack')}
        </NavLink>
      </div>
    </nav>
  );
}
